import React, { FC, MouseEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppBar,
  Button,
  ButtonGroup,
  ClickAwayListener,
  Grid,
  Grow,
  ListItemIcon,
  ListItemText,
  MenuList,
  MenuItem,
  Paper,
  Popper,
  Toolbar,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useFormikContext } from 'formik';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ScheduleIcon from '@material-ui/icons/Schedule';
import AddToPhotosIcon from '@material-ui/icons/AddToPhotos';

import { ShareType } from 'api';
import { useContent } from 'hooks';
import { UserAvatarGroup } from 'components';
import { getFullName } from 'helpers';

import Schedule from './Schedule';
import { Share } from '../types';

interface Props {
  onClose: () => void;
  position: 'fixed' | 'absolute' | 'relative' | 'static' | 'sticky';
}

const useStyles = makeStyles(theme => ({
  appBar: {
    top: 'auto',
    bottom: 0,
  },
  arrowButton: {
    padding: 0,
  },
  groupedHorizontal: {
    '&:not(:first-child)': {
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
      borderTopRightRadius: 24,
      borderBottomRightRadius: 24,
    },
    '&:not(:last-child)': {
      borderTopLeftRadius: 24,
      borderBottomLeftRadius: 24,
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
    },
  },
  disabled: {},
  groupedContainedHorizontal: {
    '&:not(:last-child)': {
      '&$disabled': {
        borderColor: 'transparent',
        borderRightColor: theme.palette.common.white,
      },
    },
  },
}));

const Footer: FC<Props> = ({ onClose, position }) => {
  const classes = useStyles();
  const content = useContent();
  const anchorRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [schedulerOpen, setSchedulerOpen] = useState(false);
  const { t } = useTranslation();
  const { values, isValid, isSubmitting, submitForm, setSubmitting, setFieldValue } =
    useFormikContext<Share>();

  const sharedByCount = content?.shared_by?.length || null;

  function handleToggle() {
    setMenuOpen(!menuOpen);
  }

  function handleClose(event: MouseEvent<Document | Element>) {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setMenuOpen(false);
  }

  async function handleShareNow() {
    setSubmitting(true);
    await submitForm();
  }

  function handleScheduleShare() {
    setSchedulerOpen(true);
  }

  async function handleSendToScheduler() {
    values.items.forEach((item, index) => {
      setFieldValue(`items[${index}].share_type`, ShareType.Auto);
    });

    setSubmitting(true);
    await submitForm();
  }

  function renderSharedByText() {
    const firstSharedBy = getFullName(content.shared_by[0]);

    if (sharedByCount === 1) {
      return t('sharing:oneSharedThis', { user1: firstSharedBy });
    }

    const secondSharedBy = getFullName(content.shared_by[1]);

    if (sharedByCount === 2) {
      return t('sharing:twoSharedThis', { user1: firstSharedBy, user2: secondSharedBy });
    }

    return t('sharing:moreSharedThis', {
      user1: firstSharedBy,
      user2: secondSharedBy,
      count: sharedByCount - 2,
    });
  }

  return (
    <AppBar component="footer" className={classes.appBar} position={position}>
      <Toolbar>
        <Grid container justify="space-between" alignItems="center">
          <Grid item xs>
            {sharedByCount && (
              <Grid container spacing={2} alignItems="center">
                <Grid item>
                  <UserAvatarGroup users={content.shared_by} />
                </Grid>
                <Grid item>
                  <Typography color="textSecondary">{renderSharedByText()}</Typography>
                </Grid>
              </Grid>
            )}
          </Grid>
          <Grid item>
            <Grid container spacing={2}>
              <Grid item>
                <Button onClick={onClose} size="large">
                  {t('common:cancel')}
                </Button>
              </Grid>
              <Grid item>
                <ButtonGroup
                  disabled={!isValid || isSubmitting}
                  classes={{
                    groupedHorizontal: classes.groupedHorizontal,
                    groupedContainedHorizontal: classes.groupedContainedHorizontal,
                    disabled: classes.disabled,
                  }}
                  variant="contained"
                  color="primary"
                  size="large"
                >
                  <Button onClick={handleShareNow}>{t('components:shareNow')}</Button>
                  <Button
                    className={classes.arrowButton}
                    ref={anchorRef}
                    size="small"
                    onClick={handleToggle}
                  >
                    <ArrowDropDownIcon />
                  </Button>
                </ButtonGroup>
                <Popper open={menuOpen} anchorEl={anchorRef.current} transition disablePortal>
                  {({ TransitionProps }) => (
                    <Grow {...TransitionProps}>
                      <Paper>
                        <ClickAwayListener onClickAway={handleClose}>
                          <MenuList>
                            <MenuItem onClick={handleScheduleShare}>
                              <ListItemIcon>
                                <ScheduleIcon />
                              </ListItemIcon>
                              <ListItemText
                                primaryTypographyProps={{ variant: 'subtitle2' }}
                                primary={t('sharing:scheduleShare')}
                              />
                            </MenuItem>
                            <MenuItem onClick={handleSendToScheduler}>
                              <ListItemIcon>
                                <AddToPhotosIcon />
                              </ListItemIcon>
                              <ListItemText
                                primaryTypographyProps={{ variant: 'subtitle2' }}
                                primary={t('sharing:sendToScheduler')}
                              />
                            </MenuItem>
                          </MenuList>
                        </ClickAwayListener>
                      </Paper>
                    </Grow>
                  )}
                </Popper>
                <Schedule open={schedulerOpen} onClose={() => setSchedulerOpen(false)} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Toolbar>
    </AppBar>
  );
};

export default Footer;
